import React, { useState, useMemo, useEffect, useRef } from "react";
import Select from "react-select";
import { Toggle, DatePicker } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import { mergeSelectStyles } from "../../../shared/darkSelectStyles";
import { formatDateOnly, isoToDate, dateToIso } from "../../../shared/formattingUtils";
import "../styles/settingsProfitAndLoss.css";

const memberSelectStyles = mergeSelectStyles({
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(42,156,228,0.18)"
      : "0 1px 2px 0 rgba(10,13,18,0.05)",
    borderColor: state.isFocused ? "#2a9ce4" : "var(--sl-border-primary, #d5d7da)",
  }),
});

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;


const EMAIL_REGEXP = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PAGE_SIZE = 8;

const IconChevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", left: 10, pointerEvents: "none" }}>
    <path d="M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3" />
  </svg>
);

const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const RecipientChip = ({ recipient, onRemove }) => (
  <span className="pnl-chip">
    <span>{recipient.label}</span>
    <button type="button" className="pnl-chip__remove" title="Remove" onClick={onRemove} aria-label={`Remove ${recipient.label}`}>
      <IconX />
    </button>
  </span>
);

const RecipientEditor = ({ recipients, teamMembers, disabled, onAdd, onRemove }) => {
  const [draft, setDraft] = useState("");
  const added = new Set(recipients.map((r) => r.email.toLowerCase()));
  const available = teamMembers.filter((m) => !added.has(m.email.toLowerCase()));

  const submitDraft = () => {
    const email = draft.trim().toLowerCase();
    if (!EMAIL_REGEXP.test(email)) {
      toastError("Enter a valid email address");
      return;
    }
    onAdd({ email }).then((ok) => {
      if (ok) setDraft("");
    });
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
        {recipients.map((r) => (
          <RecipientChip key={r.email} recipient={r} onRemove={() => onRemove(r.email)} />
        ))}
        <input
          type="email"
          className="pnl-input"
          style={{ flex: 1, minWidth: 160 }}
          placeholder="name@venue.com"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitDraft();
            }
          }}
        />
        <button type="button" className="pnl-btn pnl-btn--primary" disabled={disabled || !draft.trim()} onClick={submitDraft}>
          Add
        </button>
        {available.length > 0 && (
          <div style={{ width: 210 }}>
            <Select
              options={available.map((m) => ({ value: m.email, label: `${m.name} (${m.email})`, userId: m.userId }))}
              value={null}
              isDisabled={disabled}
              isSearchable
              placeholder="Add a team member…"
              styles={memberSelectStyles}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              classNamePrefix="pnl-member-select"
              onChange={(opt) => {
                if (opt) onAdd({ email: opt.value, user_id: opt.userId });
              }}
            />
          </div>
        )}
      </div>
      {recipients.length === 0 && (
        <div className="small text-muted mt-2">No recipients yet — the report won't go anywhere.</div>
      )}
    </div>
  );
};

const VenueRow = ({ venue, teamMembers, timeOptions, open, onToggleOpen, run }) => {
  const enabled = venue.enabled;
  const recipients = venue.recipients || [];
  const avatars = recipients.slice(0, 3);
  const overflow = recipients.length > 3 ? `+${recipients.length - 3} more` : "";
  const recipientSummary = !enabled ? "—" : recipients.length === 0 ? "None yet" : overflow;

  return (
    <div className="pnl-row" style={{ background: open ? "var(--sl-bg-secondary, #fcfcfd)" : "transparent" }}>
      <div
        className="pnl-row__main"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${venue.name} report settings`}
        onClick={onToggleOpen}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleOpen();
          }
        }}
      >
        <span className={`pnl-caret${open ? " pnl-caret--open" : ""}`}>
          <IconChevron />
        </span>
        <span className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
          <span className="pnl-dot" style={{ background: enabled ? venue.dot : "var(--sl-border-primary, #d5d7da)" }} />
          <span className="pnl-name" style={{ color: enabled ? "var(--sl-text-primary, #333)" : "var(--sl-text-tertiary, #717680)" }}>
            {venue.name}
          </span>
          {venue.customized && <span className="pnl-badge-custom">Custom</span>}
        </span>
        <span onClick={(e) => e.stopPropagation()}>
          <span className="custom-control custom-switch custom-switch-md">
            <input
              type="checkbox"
              className="custom-control-input"
              id={`pnl-toggle-${venue.id}`}
              checked={enabled}
              onChange={(e) => run({ command: "toggle_venue", venue_ownership_id: venue.id, enabled: e.target.checked }, `Report ${e.target.checked ? "on" : "off"} for ${venue.name}`)}
            />
            <label className="custom-control-label" htmlFor={`pnl-toggle-${venue.id}`} />
          </span>
        </span>
        <span className="pnl-col-time" style={{ fontSize: 14, color: enabled ? "var(--sl-text-secondary, #535862)" : "var(--sl-text-quaternary, #a4a7ae)" }}>
          {enabled ? venue.sendTimeLabel : "—"}
        </span>
        <span className="pnl-col-recipients d-flex align-items-center" style={{ gap: 6, minWidth: 0 }}>
          {enabled && avatars.length > 0 && (
            <span className="pnl-avatars">
              {avatars.map((a) => (
                <span key={a.email} className="pnl-avatar" title={a.label}>
                  {a.initials}
                </span>
              ))}
            </span>
          )}
          <span style={{ fontSize: 14, color: enabled ? "var(--sl-text-secondary, #535862)" : "var(--sl-text-quaternary, #a4a7ae)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {recipientSummary}
          </span>
        </span>
        <span className="pnl-col-last" style={{ textAlign: "right", fontSize: 14, color: enabled && venue.lastSend ? "var(--sl-text-secondary, #535862)" : "var(--sl-text-quaternary, #a4a7ae)" }}>
          {!enabled ? "—" : venue.lastSend || "Never"}
        </span>
      </div>

      {open && (
        <div className="pnl-panel">
          <div className="pnl-panel__inner">
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 12 }}>
                <span className="pnl-eyebrow" style={{ marginBottom: 0 }}>Recipients</span>
                <span className="small text-muted">
                  {venue.customized ? "Custom for this venue" : "Using team defaults — editing here makes it custom"}
                </span>
              </div>
              <RecipientEditor
                recipients={recipients}
                teamMembers={teamMembers}
                onAdd={(payload) => run({ command: "add_venue_recipient", venue_ownership_id: venue.id, ...payload }, "Recipient added")}
                onRemove={(email) => run({ command: "remove_venue_recipient", venue_ownership_id: venue.id, email }, "Recipient removed")}
              />
              <div className="d-flex align-items-center flex-wrap mt-3" style={{ gap: 12 }}>
                <label className="d-flex align-items-center mb-0" style={{ gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--sl-text-secondary, #414651)" }}>Send at</span>
                  <select
                    className="pnl-select"
                    style={{ width: 160 }}
                    value={venue.sendTime}
                    onChange={(e) => run({ command: "set_venue_time", venue_ownership_id: venue.id, send_time: e.target.value }, "Send time updated")}
                  >
                    {timeOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
                {venue.customized && (
                  <>
                    <button type="button" className="pnl-btn" onClick={() => run({ command: "reset_venue", venue_ownership_id: venue.id }, "Reset to team defaults")}>
                      Reset to team defaults
                    </button>
                    <button type="button" className="pnl-btn pnl-btn--brand" onClick={() => run({ command: "make_venue_default", venue_ownership_id: venue.id }, "Team defaults updated")}>
                      Make this the team default
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={{ paddingTop: 20, borderTop: "1px solid var(--sl-border-secondary, #e9eaeb)" }}>
              <div className="pnl-eyebrow">Recent sends</div>
              {venue.recentSends && venue.recentSends.length > 0 ? (
                <div className="pnl-recent">
                  {venue.recentSends.map((l, i) => (
                    <div key={i} className="pnl-recent__row">
                      <span style={{ fontWeight: 600, color: "var(--sl-text-primary, #333)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.event}</span>
                      <span className="small text-muted" style={{ flex: "0 0 auto" }}>{l.sent}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="small text-muted">No reports sent for this venue yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsProfitAndLoss = (props) => {
  const { settingsPath, updatePath } = props;
  const [data, setData] = useState({
    timeOptions: props.timeOptions,
    teamMembers: props.teamMembers,
    teamDefaults: props.teamDefaults,
    venues: props.venues,
    venuesCount: props.venuesCount,
    followingDefaultsCount: props.followingDefaultsCount,
    ancillaryLineItems: props.ancillaryLineItems,
  });
  const [ancillaryStartOn, setAncillaryStartOn] = useState(props.ancillaryLineItems?.startOn || "");
  const savedAncillaryStartOn = useRef(props.ancillaryLineItems?.startOn || "");
  const displayedAncillaryStartOn = useRef(props.ancillaryLineItems?.startOn || "");
  const latestAncillaryStartOnSave = useRef(0);
  const [ancillaryPickerNonce, setAncillaryPickerNonce] = useState(0);
  const ancillaryStartOnDate = useMemo(() => isoToDate(ancillaryStartOn), [ancillaryStartOn]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("On");
  const [openRow, setOpenRow] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const run = (payload, successMessage) =>
    client
      .patch(updatePath, payload)
      .then((res) => {
        setData((prev) => ({ ...prev, ...res.data }));
        if (successMessage) toastSuccess(successMessage);
        return true;
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.join(", ") || "An error occurred";
        toastError(message);
        return false;
      });

  const { venues, teamMembers, timeOptions, teamDefaults, ancillaryLineItems } = data;
  const onCount = venues.filter((v) => v.enabled).length;
  const offCount = venues.length - onCount;

  const ancillaryFlowEnabled = Boolean(ancillaryLineItems.enabled);
  const ancillaryToggleDisabled = !ancillaryFlowEnabled && !ancillaryLineItems.startOn;

  useEffect(() => {
    displayedAncillaryStartOn.current = ancillaryStartOn;
  });

  useEffect(() => {
    const serverStartOn = ancillaryLineItems.startOn || "";
    savedAncillaryStartOn.current = serverStartOn;
    if (serverStartOn === displayedAncillaryStartOn.current) return;

    showAncillaryStartOn(serverStartOn);
  }, [ancillaryLineItems.startOn]);

  const saveAncillaryFlow = (enabled, successMessage) =>
    run({ command: "set_ancillary_line_item_flow", enabled }, successMessage);

  const saveAncillaryStartOn = (startOn, successMessage) =>
    run({ command: "set_ancillary_line_item_flow", start_on: startOn }, successMessage);

  const showAncillaryStartOn = (startOn) => {
    setAncillaryStartOn(startOn);
    setAncillaryPickerNonce((nonce) => nonce + 1);
  };

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    return venues.filter(
      (v) =>
        (!q || (v.name || "").toLowerCase().includes(q)) &&
        (filter === "All" || (filter === "On" ? v.enabled : !v.enabled))
    );
  }, [venues, query, filter]);

  const visible = showAll || query ? matched : matched.slice(0, PAGE_SIZE);
  const canExpandList = matched.length > PAGE_SIZE && !query;

  const filters = [
    { label: "On", count: onCount },
    { label: "Off", count: offCount },
    { label: "All", count: venues.length },
  ];

  return (
    <div className="mt-3 pnl-wrap">
      <div className="d-flex align-items-center mb-3" style={{ gap: 6, fontSize: 14 }}>
        <a href={settingsPath} className="settings-breadcrumb-link">Settings</a>
        <span style={{ color: "var(--sl-text-quaternary, #d5d7da)" }}>›</span>
        <span className="settings-breadcrumb-current">P&amp;L</span>
      </div>

      <div className="d-flex align-items-start justify-content-between flex-wrap mb-4" style={{ gap: 16 }}>
        <div>
          <h3 className="mb-1">Automated report emails</h3>
          <p className="mb-0 settings-subtitle" style={{ maxWidth: 640 }}>
            One row per venue: whether the Show Profitability Report sends, when, and who gets it. Open a row to edit.
          </p>
        </div>
        <span className="pnl-saved-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          Changes save automatically
        </span>
      </div>

      <div className="pnl-card mb-4">
        <div className="pnl-card__head">
          <div>
            <h2 className="pnl-card__title">Team defaults</h2>
            <p className="mb-0 small text-muted">Every venue uses these unless you customize it. New venues inherit them too.</p>
          </div>
          <span className="pnl-usage-pill">{data.followingDefaultsCount} of {data.venuesCount} venues follow defaults</span>
        </div>
        <div className="d-flex align-items-start flex-wrap pnl-card__body">
          <label className="mb-0">
            <span className="pnl-eyebrow">Send time</span>
            <select
              className="pnl-select"
              style={{ width: 160 }}
              value={teamDefaults.sendTime}
              onChange={(e) => run({ command: "set_default_time", send_time: e.target.value }, "Default send time updated")}
            >
              {timeOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          <div style={{ flex: 1, minWidth: 280 }}>
            <span className="pnl-eyebrow">Recipients</span>
            <RecipientEditor
              recipients={teamDefaults.recipients}
              teamMembers={teamMembers}
              onAdd={(payload) => run({ command: "add_default_recipient", ...payload }, "Default recipient added")}
              onRemove={(email) => run({ command: "remove_default_recipient", email }, "Default recipient removed")}
            />
          </div>
        </div>
      </div>

      <div className="pnl-card mb-4">
        <div className="pnl-card__head">
          <div>
            <h2 className="pnl-card__title">Offer budget lines in the P&amp;L</h2>
            <p className="mb-0 small text-muted">
              Ancillary income and expenses entered on an offer&apos;s budget appear in the event P&amp;L, so you don&apos;t re-enter them in Finances. Shows before the start date are left as they are. If you already track any of these in Finances, remove them there first — a cost entered in both places is counted twice.
            </p>
          </div>
        </div>
        <div className="d-flex align-items-start flex-wrap pnl-card__body">
          <div>
            <Toggle
              id="pnl-ancillary-flow-toggle"
              label="Flow into the P&L"
              checked={ancillaryFlowEnabled}
              disabled={ancillaryToggleDisabled}
              aria-describedby={ancillaryToggleDisabled ? "pnl-ancillary-flow-hint" : undefined}
              onChange={(e) =>
                saveAncillaryFlow(
                  e.target.checked,
                  e.target.checked
                    ? "Offer budget lines will flow into the P&L"
                    : "Offer budget lines will no longer flow into the P&L"
                )
              }
            />
            {ancillaryToggleDisabled && (
              <div className="small text-muted mt-1" id="pnl-ancillary-flow-hint" role="status">Choose a start date first</div>
            )}
          </div>
          <div>
            <DatePicker
              key={`pnl-ancillary-start-on-${ancillaryPickerNonce}`}
              id="pnl-ancillary-start-on"
              label="Start date"
              placeholder="Select date"
              min={isoToDate(ancillaryLineItems.startOnMin)}
              max={isoToDate(ancillaryLineItems.startOnMax)}
              value={ancillaryStartOnDate}
              onChange={(selected) => {
                const startOn = dateToIso(selected);
                if (!startOn) {
                  toastError("A start date is required — pick a new date to move it");
                  showAncillaryStartOn(savedAncillaryStartOn.current);
                  return;
                }
                setAncillaryStartOn(startOn);
                latestAncillaryStartOnSave.current += 1;
                const saveNumber = latestAncillaryStartOnSave.current;
                saveAncillaryStartOn(
                  startOn,
                  ancillaryFlowEnabled
                    ? `Offer budget lines will flow into the P&L for shows on or after ${formatDateOnly(startOn)}`
                    : "Start date saved"
                ).then((ok) => {
                  if (!ok && saveNumber === latestAncillaryStartOnSave.current) {
                    showAncillaryStartOn(savedAncillaryStartOn.current);
                  }
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="pnl-card">
        <div className="pnl-card__head">
          <div className="pnl-filters">
            {filters.map((f) => (
              <button
                key={f.label}
                type="button"
                className={`pnl-filter${filter === f.label ? " pnl-filter--active" : ""}`}
                onClick={() => { setFilter(f.label); setShowAll(false); setOpenRow(null); }}
              >
                {f.label} {f.count}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <IconSearch />
              <input
                type="text"
                className="pnl-input pnl-search"
                placeholder="Search venues"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button type="button" className="pnl-btn" onClick={() => run({ command: "all_on" }, "All venues on")}>All on</button>
            <button type="button" className="pnl-btn" onClick={() => run({ command: "all_off" }, "All venues off")}>All off</button>
          </div>
        </div>

        <div className="pnl-table-head">
          <span />
          <span>Venue</span>
          <span>Report</span>
          <span>Send time</span>
          <span>Recipients</span>
          <span style={{ textAlign: "right" }}>Last send</span>
        </div>

        {visible.map((venue) => (
          <VenueRow
            key={venue.id}
            venue={venue}
            teamMembers={teamMembers}
            timeOptions={timeOptions}
            open={openRow === venue.id}
            onToggleOpen={() => setOpenRow(openRow === venue.id ? null : venue.id)}
            run={run}
          />
        ))}

        {matched.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center" }} className="text-muted">
            No venues match{query ? ` “${query}”` : ""}.
          </div>
        )}

        <div className="pnl-footer">
          <span className="small text-muted">Showing {visible.length} of {matched.length} venues</span>
          {canExpandList && (
            <button type="button" className="pnl-btn" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer" : `Show all ${matched.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsProfitAndLoss;
